import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import DefaultPagination from '../../../../../components/Accessibility/DefaultPagination';
import { useAccessibility } from '../../../../../Store/contexts/AccessibilityContext';
import { MOBILE_BREAKPOINT } from '../../transcricaoAnexoUtils';
import {
  sxAnexosTableCell,
  sxAnexosTableContainer,
  sxAnexosTableHeaderCell,
  sxAnexosTableRow,
} from '../TranscricaoAnexos/sxTranscricaoAnexos';
import { mapLinhaToMobileRows } from '../../utils/transcricaoDadosPedidoUtils';
import { RevisaoColuna, RevisaoLinha } from '../../utils/transcricaoRevisaoTypes';
import RevisaoSectionCard from './RevisaoSectionCard';
import './revisaoSectionCard.css';

export type { RevisaoColuna, RevisaoLinha };

const PAGE_SIZE = 10;

interface RevisaoSectionTableProps {
  titulo: string;
  colunas: RevisaoColuna[];
  linhas: RevisaoLinha[];
  showEditColumn?: boolean;
  onEditClick?: (linhaId: string | number) => void;
}

const RevisaoSectionTable: React.FC<RevisaoSectionTableProps> = ({
  titulo,
  colunas,
  linhas,
  showEditColumn = false,
  onEditClick,
}) => {
  const accessibility = useAccessibility();
  const isMobileLayout = useMediaQuery(MOBILE_BREAKPOINT);
  const [page, setPage] = useState(1);

  const colunasVisiveis = showEditColumn
    ? colunas
    : colunas.filter((col) => !col.isEdit);
  const dataColunas = colunas.filter((col) => !col.isEdit);

  const totalPages = Math.max(1, Math.ceil(linhas.length / PAGE_SIZE));
  const precisaPaginacao = linhas.length > PAGE_SIZE;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const linhasPaginadas = precisaPaginacao
    ? linhas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : linhas;

  const linhasParaExibir = linhas.length > 0
    ? linhas
    : [{ id: 'empty', cells: {} }];

  if (isMobileLayout) {
    return (
      <section className="transcricao-revisao-section transcricao-revisao-section--mobile">
        <Typography
          style={accessibility.textStyleAccessibility}
          className="transcricao-revisao-section-title"
        >
          <strong>{titulo}</strong>
        </Typography>

        <div
          className={
            linhasParaExibir.length > 1
              ? 'transcricao-revisao-mobile-cards'
              : undefined
          }
        >
          {linhasParaExibir.map((linha) => (
            <RevisaoSectionCard
              key={linha.id}
              rows={mapLinhaToMobileRows(dataColunas, linha)}
              onEditClick={
                showEditColumn && onEditClick && linha.id !== 'empty'
                  ? () => onEditClick(linha.id)
                  : undefined
              }
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="transcricao-revisao-section">
      <Typography
        style={accessibility.textStyleAccessibility}
        className="transcricao-revisao-section-title"
      >
        <strong>{titulo}</strong>
      </Typography>
      <TableContainer
        style={accessibility.contrastBorderAccessibility}
        className="container-table-result-exam transcricao-revisao-table"
        sx={sxAnexosTableContainer}
        component={Paper}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {colunasVisiveis.map((coluna) => (
                <TableCell
                  key={coluna.key}
                  component="th"
                  scope="col"
                  align={coluna.align ?? 'left'}
                  style={accessibility.textStyleAccessibility}
                  sx={sxAnexosTableHeaderCell}
                >
                  <b>{coluna.label}</b>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {linhas.length === 0 ? (
              <TableRow sx={sxAnexosTableRow}>
                {colunasVisiveis.map((coluna) => (
                  <TableCell
                    key={coluna.key}
                    style={accessibility.textStyleAccessibility}
                    sx={sxAnexosTableCell}
                    align={coluna.align ?? 'left'}
                  >
                    {coluna.isEdit ? (
                      <IconButton
                        aria-label="Editar"
                        size="small"
                        sx={{ color: 'rgba(0, 153, 93, 1)', padding: '4px' }}
                        disabled={!onEditClick}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </IconButton>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ) : (
              linhasPaginadas.map((linha) => (
                <TableRow key={linha.id} sx={sxAnexosTableRow}>
                  {colunasVisiveis.map((coluna) => (
                    <TableCell
                      key={coluna.key}
                      style={accessibility.textStyleAccessibility}
                      sx={sxAnexosTableCell}
                      align={coluna.align ?? 'left'}
                    >
                      {coluna.isEdit ? (
                        <IconButton
                          aria-label="Editar"
                          size="small"
                          sx={{ color: 'rgba(0, 153, 93, 1)', padding: '4px' }}
                          onClick={() => onEditClick?.(linha.id)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </IconButton>
                      ) : (
                        linha.cells[coluna.key] ?? '—'
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {precisaPaginacao && (
        <div className="transcricao-revisao-pagination-area">
          <Typography
            style={accessibility.textStyleAccessibility}
            className="transcricao-revisao-contagem"
          >
            {`Mostrando ${linhasPaginadas.length} de ${linhas.length} itens`}
          </Typography>
          <div className="transcricao-revisao-pagination">
            <DefaultPagination
              currentPage={page}
              totalPages={totalPages}
              handlePageChange={setPage}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default RevisaoSectionTable;
